// src/pages/Register.js

import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import Container from "react-bootstrap/Container";
import Card from "react-bootstrap/Card";
import Button from "react-bootstrap/Button";
import Form from "react-bootstrap/Form";
import Alert from "react-bootstrap/Alert";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Badge from "react-bootstrap/Badge";
import ProgressBar from "react-bootstrap/ProgressBar";
import { ShieldCheck, KeyRound, ArrowRight } from "lucide-react";

const API_BASE = "https://app.fintogo.ai/api";
const brandBlue = "#1E88E5";
const brandGreen = "#2ED08A";

export default function Register() {
  const navigate = useNavigate();
  const [activationCode, setActivationCode] = useState("");
  const [validating, setValidating] = useState(false);
  const [error, setError] = useState(null);

  const handleValidateCode = async (e) => {
    e.preventDefault();
    setError(null);
    setValidating(true);

    try {
      const res = await fetch(
        `${API_BASE}/activation/validate?code=${encodeURIComponent(
          activationCode.trim()
        )}`
      );
      const data = await res.json();

      if (!res.ok || !data?.ok) {
        throw new Error(data?.error || "validation_failed");
      }

      // Routing basierend auf 'role'
      if (data.role === "teacher") {
        navigate(`/register-teacher?code=${encodeURIComponent(activationCode.trim())}`);
      } else if (data.role === "student") {
        navigate(`/register-student?code=${encodeURIComponent(activationCode.trim())}`);
      } else {
        throw new Error("unknown_role");
      }
    } catch (err) {
      const map = {
        invalid_code: "Der Aktivierungscode ist ungültig.",
        code_expired: "Der Aktivierungscode ist abgelaufen.",
        code_exhausted: "Der Aktivierungscode wurde bereits vollständig genutzt.",
        unknown_role: "Unbekannte Benutzerrolle im Aktivierungscode.",
      };
      setError(
        map[err.message] || "Validierung fehlgeschlagen. Bitte erneut versuchen."
      );
    } finally {
      setValidating(false);
    }
  };

  return (
    <div
      className="d-flex align-items-center justify-content-center"
      style={{
        minHeight: "100vh",
        width: "100vw",
        margin: 0,
        padding: 0,
        background: "linear-gradient(to bottom right, #e0f0ff, white)",
        overflow: "auto",
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 0,
      }}
    >
      <Container style={{ maxWidth: 900, padding: "2rem 1rem" }}>
        <Row className="justify-content-center">
          <Col lg={10} xl={9}>
            <Card className="shadow border-0">
              <Card.Body className="p-4 p-md-5">
                {/* Header */}
                <div className="d-flex align-items-center mb-4 gap-3">
                  <div
                    className="rounded-circle d-flex justify-content-center align-items-center"
                    style={{
                      width: 48,
                      height: 48,
                      background: brandBlue,
                    }}
                  >
                    <ShieldCheck className="text-white" size={24} />
                  </div>
                  <div>
                    <h1 className="h4 mb-1">Willkommen bei FintoGo</h1>
                    <p className="mb-0 text-muted">
                      Gib deinen Aktivierungscode ein, um fortzufahren.
                    </p>
                  </div>
                </div>

                {error && (
                  <Alert variant="danger" className="mb-4">
                    {error}
                  </Alert>
                )}

                {/* Step indicator */}
                <div className="mb-4 d-flex justify-content-between align-items-center">
                  <div className="d-flex align-items-center gap-2">
                    <Badge bg="light" text="dark">
                      Schritt 1 / 2
                    </Badge>
                    <span className="text-muted small">
                      Aktivierungscode prüfen
                    </span>
                  </div>
                  <div style={{ minWidth: 120 }}>
                    <ProgressBar
                      now={50}
                      variant="success"
                      style={{ height: 6 }}
                    />
                  </div>
                </div>

                <Form onSubmit={handleValidateCode}>
                  <Form.Group className="mb-3" controlId="activationCode">
                    <Form.Label>Aktivierungscode</Form.Label>
                    <div className="d-flex align-items-center border rounded px-2">
                      <KeyRound className="text-muted me-2" size={18} />
                      <Form.Control
                        type="text"
                        placeholder="z.B. ABCD-1234"
                        value={activationCode}
                        onChange={(e) =>
                          setActivationCode(e.target.value.toUpperCase())
                        }
                        className="border-0 shadow-none"
                      />
                    </div>
                    <Form.Text className="text-muted">
                      Du findest den Code z.B. auf einem Infoblatt oder in
                      einer Nachricht deiner Lehrkraft.
                    </Form.Text>
                  </Form.Group>

                  <div className="d-grid">
                    <Button
                      type="submit"
                      disabled={validating || !activationCode.trim()}
                      style={{
                        background: brandBlue,
                        borderColor: brandBlue,
                      }}
                    >
                      {validating ? (
                        "Wird geprüft..."
                      ) : (
                        <>
                          Code prüfen
                          <ArrowRight className="ms-2" size={18} />
                        </>
                      )}
                    </Button>
                  </div>
                </Form>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
}
